import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { EnvironmentVariables } from "@/modules/common/types";
import {
  AddStageDto,
  EditStageDto,
  GetPropertyFieldsResponse,
  StatusesResponse,
  UpdateStageOrderDto,
} from "../dtos";
import { AxiosResponse } from "axios";
import {
  ActiveStagesDto,
  AddTrackStatusDto,
  EditTrackStatusDto,
  TagNamesDto,
  TrackStage,
  TrackStatusDto,
  TrackStatusUpdatesDto,
  TrackTypeStage,
} from "../types";
import { FiltersMultiResponse } from "@/modules/common/dtos/index.v2";
import {
  CreateTrackStatusForm,
  UpdateTrackStatusForm,
} from "../hooks/track-type-status-form.hook";
import { TrackStatusFormField } from "@/modules/property-tracks/dtos/property-stage";

export const addTrackStatuses = async ({
  payload,
}: {
  payload: {
    statuses: AddTrackStatusDto[];
  };
}): Promise<AxiosResponse<TrackStatusDto[]>> => {
  const apiUrl = brandUrl<TrackStatusDto[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses/create-many`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: payload,
    },
  });
};

export const addTrackStatus = async ({
  payload,
}: {
  payload: AddTrackStatusDto;
}): Promise<AxiosResponse<TrackStatusDto>> => {
  const apiUrl = brandUrl<TrackStatusDto>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: payload,
    },
  });
};

export const editTrackStatus = async ({
  statusId,
  payload,
}: {
  statusId: number;
  payload: EditTrackStatusDto;
}): Promise<AxiosResponse<TrackStatusDto>> => {
  const apiUrl = brandUrl<TrackStatusDto>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses/${statusId}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: payload,
    },
  });
};

export const addStage = async (body: AddStageDto) => {
  const apiUrl = brandUrl<TrackStage>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-stages`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  }) as Promise<TrackStage>;
};

export const getTrackTypeStages = async (trackTypeId: number) => {
  const apiUrl = brandUrl<TrackTypeStage>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-types/${trackTypeId}/track-stages`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: TrackTypeStage }>;
};

export const getActiveTrackStages = async (trackTypeId: number) => {
  const apiUrl = brandUrl<ActiveStagesDto[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-types/${trackTypeId}/active-track-stages`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: ActiveStagesDto[] }>;
};

export const updateStageOrder = async (
  trackTypeId: number,
  body: UpdateStageOrderDto[],
) => {
  const apiUrl = brandUrl<void>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-types/${trackTypeId}/change-stages-ordering`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: {
        stages: body,
      },
    },
  }) as Promise<any>;
};

export const editStage = async ({
  stageId,
  payload,
}: {
  stageId: number;
  payload: EditStageDto;
}): Promise<AxiosResponse<TrackStage>> => {
  const apiUrl = brandUrl<TrackStage>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-stages/${stageId}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: payload,
    },
  });
};

export const getLeadStatuses = async ({
  params,
}: {
  params?: {
    search?: string;
    savedIds?: number[];
    trackTypeId?: number;
    leadTypeId?: number;
    mapToTrackStatus?: boolean;
    organizationUIds?: string;
  };
}): Promise<AxiosResponse<FiltersMultiResponse[]>> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/lead-statuses/filter-options`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
        savedIds: params?.savedIds?.join(","),
      },
    },
  });
};

export const getTrackStatusUpdates = async (trackUid: string) => {
  const apiUrl = brandUrl<TrackStatusUpdatesDto[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUid}/status-updates`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: TrackStatusUpdatesDto[] }>;
};

export const getTagsNames = async () => {
  const apiUrl = brandUrl<TagNamesDto[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses/tags/names`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: TagNamesDto[] }>;
};

export const addTrackStatusForm = async (data: CreateTrackStatusForm) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-status-forms`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const getTrackStatusForms = async ({
  params,
}: {
  params?: {
    search?: string;
  };
}) => {
  const apiUrl = brandUrl<any[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-status-forms/`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  });
};

export const updateTrackStatusForm = async (
  id: number,
  data: UpdateTrackStatusForm,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-status-forms/${id}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data,
    },
  });
};

export const deleteTrackStatusForm = async (id: number) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-status-forms/${id}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const getTrackStatusFormFields = async ({
  params,
}: {
  params?: {
    name?: string;
    page?: number;
    pageSize?: number;
  };
}) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/property-fields`,
  );
  return fetchFromBrandedUrl<{ data: GetPropertyFieldsResponse }>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  });
};

export const fetchStatuses = async (params: {
  name?: string;
  pageSize?: number;
}) => {
  const apiUrl = brandUrl<StatusesResponse>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<{ data: StatusesResponse }>;
};

export const linkTrackStatusForm = async ({
  statusId,
  formId,
}: {
  statusId: number;
  formId: number;
}) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses/${statusId}/forms/${formId}/link`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  });
};

export const unlinkTrackStatusForm = async ({
  statusId,
  formId,
}: {
  statusId: number;
  formId: number;
}) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses/${statusId}/forms/${formId}/unlink`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "DELETE",
    },
  });
};

export const addTrackStatusFormField = async (
  data: Omit<TrackStatusFormField, "uid" | "createdAt" | "updatedAt">,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/property-fields`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const updateTrackStatusFormField = async (
  id: number,
  data: Omit<
    TrackStatusFormField,
    "id" | "uid" | "createdAt" | "updatedAt" | "isDropDown"
  >,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/property-fields/${id}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data,
    },
  });
};

export const deleteTrackStatusFormField = async (id: number) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/property-fields/${id}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};
